export const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const FILES = 'abcdefgh';

export function piecesFromFen(fen) {
  const pieces = {};
  const ranks = typeof fen === 'string' ? fen.split(' ')[0].split('/') : [];
  if (ranks.length !== 8) return pieces;
  ranks.forEach((rank, row) => {
    let column = 0;
    for (const character of rank) {
      if (/^[1-8]$/.test(character)) column += Number(character);
      else {
        if (/^[prnbqkPRNBQK]$/.test(character) && column < 8) pieces[`${FILES[column]}${8 - row}`] = character;
        column += 1;
      }
    }
  });
  return pieces;
}

function sideOf(piece) { return piece === piece.toUpperCase() ? 'w' : 'b'; }

function moveSquares(move) {
  if (!move || !/^[a-h][1-8][a-h][1-8][qrbn]?$/.test(move.uci || '')) return null;
  return { from: move.uci.slice(0, 2), to: move.uci.slice(2, 4) };
}

function capturedOnMove(before, move) {
  const squares = moveSquares(move);
  if (!squares || !before[squares.from]) return null;
  const mover = before[squares.from];
  let capturedSquare = squares.to;
  let victim = before[capturedSquare];
  if (!victim && mover.toLowerCase() === 'p' && squares.from[0] !== squares.to[0]) {
    capturedSquare = `${squares.to[0]}${squares.from[1]}`;
    victim = before[capturedSquare];
    if (!victim || victim.toLowerCase() !== 'p') return null;
  }
  if (!victim || sideOf(victim) === sideOf(mover)) return null;
  return { piece: victim, square: capturedSquare, capturedBy: sideOf(mover), ply: move.ply };
}

// Track actual victims at each historical move. A promoted pawn is never a victim;
// a promoted queen captured later is recorded as the queen present in that FEN.
export function capturedPieces(initialFen, moves, position = moves.length) {
  const captures = { w: [], b: [] };
  let before = piecesFromFen(initialFen || STARTING_FEN);
  const selected = moves.slice(0, Math.max(0, Math.min(position, moves.length)));
  selected.forEach(move => {
    const capture = capturedOnMove(before, move);
    if (capture) captures[capture.capturedBy].push(capture);
    before = piecesFromFen(move.fen);
  });
  return captures;
}

function samePosition(left, right) {
  return left.fen === right.fen && left.gameId === right.gameId && left.position === right.position;
}

export function isSameBoardSnapshot(left, right) { return !!left && samePosition(left, right); }

// Animate only an adjacent, verified edge in this game's recorded history.
// Initial loads, reconnections, different games, and multi-move jumps stay still.
export function moveTransition(previous, next) {
  if (!previous || !previous.hasState || !next.hasState || previous.gameId !== next.gameId || previous.replaying !== next.replaying) return null;
  if (!next.replaying && (![previous.connection, next.connection].every(value => ['live', 'polling'].includes(value)))) return null;
  const difference = next.position - previous.position;
  if (Math.abs(difference) !== 1) return null;
  const moveIndex = Math.max(previous.position, next.position) - 1;
  const history = next.moves;
  const move = history[moveIndex];
  const squares = moveSquares(move);
  if (!squares) return null;
  const fromFen = moveIndex > 0 ? history[moveIndex - 1].fen : next.initialFen || STARTING_FEN;
  const toFen = move.fen;
  if (difference === 1 ? previous.fen !== fromFen || next.fen !== toFen : previous.fen !== toFen || next.fen !== fromFen) return null;
  const before = piecesFromFen(fromFen);
  const after = piecesFromFen(toFen);
  const piece = before[squares.from];
  if (!piece || !after[squares.to]) return null;
  const backwards = difference === -1;
  const motions = [{ id: 'main', piece: backwards ? after[squares.to] : piece, from: backwards ? squares.to : squares.from, to: backwards ? squares.from : squares.to }];
  if (piece.toLowerCase() === 'k' && Math.abs(FILES.indexOf(squares.to[0]) - FILES.indexOf(squares.from[0])) === 2) {
    const rank = squares.from[1];
    const rookFrom = `${squares.to[0] === 'g' ? 'h' : 'a'}${rank}`;
    const rookTo = `${squares.to[0] === 'g' ? 'f' : 'd'}${rank}`;
    if (!before[rookFrom] || before[rookFrom].toLowerCase() !== 'r' || after[rookTo] !== before[rookFrom]) return null;
    motions.push({ id: 'rook', piece: before[rookFrom], from: backwards ? rookTo : rookFrom, to: backwards ? rookFrom : rookTo });
  }
  const victim = capturedOnMove(before, move);
  const capture = victim ? { ...victim, restoring: backwards } : null;
  return { motions, capture, hiddenSquares: [...motions.map(motion => motion.to), ...(capture && capture.restoring ? [capture.square] : [])], backwards };
}

export function squareCoordinates(square) { return { file: FILES.indexOf(square[0]), rank: 8 - Number(square[1]) }; }

export function pieceName(piece) {
  const names = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king' };
  return `${sideOf(piece) === 'w' ? 'White' : 'Black'} ${names[piece.toLowerCase()]}`;
}

export function materialBalance(fen) {
  const values = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };
  return Object.values(piecesFromFen(fen)).reduce((balance, piece) => balance + values[piece.toLowerCase()] * (sideOf(piece) === 'w' ? 1 : -1), 0);
}
